<script setup>
import { formatPhone } from '@/lib/phone.js'
import RecordLink from '@/components/record/RecordLink.vue'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { authFetch } from '@addictivegroup/addictive-auth'
import { api } from '@/lib/http.js'
import { ENTITIES } from '@/lib/entities.js'
import { useMeta } from '@/stores/meta.js'
import { reportError } from '@/composables/useSave.js'
import RefPicker from '@/components/form/RefPicker.vue'
import UiButton from '@/components/ui/UiButton.vue'
import UiBadge from '@/components/ui/UiBadge.vue'
import QuickCampaignDialog from './QuickCampaignDialog.vue'

/**
 * Who is on a marketing list (D-139), with their e-mail and marketing e-mail consent: added
 * and removed here for a static list, shown as they match for a dynamic one; exported, or
 * given an activity each in a quick campaign.
 */
const props = defineProps({ record: { type: Object, required: true }, canEdit: Boolean, canQuick: Boolean })
const emit = defineEmits(['changed'])
const { t } = useI18n()
const meta = useMeta()
const rows = ref([])
const total = ref(0)
const page = ref(1)
const lastPage = ref(1)
const q = ref('')
const adding = ref(null)
const quick = ref(false)
const isStatic = computed(() => props.record.list_type !== 'dynamic')
const type = computed(() => props.record.member_type)

async function load() {
  try {
    const res = await api(`/marketing-lists/${props.record.uuid}/members`, { query: { q: q.value || undefined, page: { number: page.value, size: 50 } } })
    rows.value = res.data
    total.value = res.meta.total
    lastPage.value = res.meta.last_page
  } catch (e) {
    reportError(e)
  }
}
onMounted(load)
watch(() => props.record.version, load)
let timer = null
watch(q, () => {
  clearTimeout(timer)
  timer = setTimeout(() => { page.value = 1; load() }, 300)
})

async function add() {
  try {
    await api(`/marketing-lists/${props.record.uuid}/members`, { method: 'POST', body: { uuids: [adding.value] } })
    adding.value = null
    await load()
    emit('changed')
  } catch (e) {
    reportError(e)
  }
}
async function remove(row) {
  try {
    await api(`/marketing-lists/${props.record.uuid}/members/${row.uuid}`, { method: 'DELETE' })
    await load()
    emit('changed')
  } catch (e) {
    reportError(e)
  }
}
async function exportCsv() {
  try {
    const response = await authFetch(`/api/v1/marketing-lists/${props.record.uuid}/export`, { headers: { Accept: 'text/csv' } })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    const a = document.createElement('a')
    a.href = URL.createObjectURL(await response.blob())
    a.download = `${props.record.name}.csv`
    a.click()
  } catch (e) {
    reportError(e)
  }
}
const consentTone = (c) => (c === 'granted' ? 'green' : c === 'withdrawn' ? 'red' : 'gray')
const consentText = (c) => t(`marketing.consent.${c || 'none'}`)
void meta
</script>

<template>
  <section class="card" data-testid="marketing-list-members">
    <header class="flex flex-wrap items-center gap-2 border-b border-default px-3 py-2">
      <h3 class="flex-1 text-sm font-semibold">{{ $t('marketing.members') }} <span class="font-normal text-faint">({{ total }})</span></h3>
      <UiButton size="xs" variant="ghost" icon="fa-file-csv" @click="exportCsv">{{ $t('marketing.export') }}</UiButton>
      <UiButton v-if="canQuick && total" size="xs" icon="fa-bolt" data-testid="quick-campaign" @click="quick = true">{{ $t('marketing.quickCampaign') }}</UiButton>
    </header>
    <p v-if="!isStatic" class="border-b border-default px-3 py-2 text-xs text-muted"><i class="fa-solid fa-filter" aria-hidden="true"></i> {{ $t('marketing.dynamicHint') }}</p>
    <div class="flex flex-wrap gap-2 border-b border-default p-3">
      <input v-model="q" type="search" class="input min-w-40 flex-1" :placeholder="$t('common.searchPlaceholder')" />
      <template v-if="isStatic && canEdit && !record.locked">
        <RefPicker v-model="adding" :entity="type" class="min-w-56 flex-1" data-testid="member-picker" />
        <UiButton variant="primary" icon="fa-plus" :disabled="!adding" data-testid="member-add" @click="add">{{ $t('common.add') }}</UiButton>
      </template>
    </div>
    <p v-if="!rows.length" class="px-3 py-3 text-sm text-faint">{{ $t('common.empty') }}</p>
    <ul v-else class="divide-y divide-[var(--border)]">
      <li v-for="row in rows" :key="row.uuid" class="flex flex-wrap items-center gap-2 px-3 py-2 text-sm" :data-member="row.label">
        <i :class="['fa-solid fa-fw text-xs', ENTITIES[type].icon, ENTITIES[type].color]" aria-hidden="true"></i>
        <RecordLink class="link min-w-32 flex-1 truncate" :entity="type" :uuid="row.uuid">{{ row.label }}</RecordLink>
        <span class="min-w-0 max-w-[45%] shrink truncate text-xs text-muted">{{ row.email || formatPhone(row.phone) || '' }}</span>
        <UiBadge v-if="type !== 'account'" :tone="consentTone(row.consent)" :title="$t('marketing.consentEmail')">{{ consentText(row.consent) }}</UiBadge>
        <UiButton v-if="isStatic && canEdit && !record.locked" size="xs" variant="ghost" icon="fa-xmark" class="hover:text-red-600" :aria-label="$t('common.remove')" @click="remove(row)" />
      </li>
    </ul>
    <footer v-if="lastPage > 1" class="flex items-center justify-end gap-2 border-t border-default px-3 py-2 text-sm">
      <UiButton size="xs" icon="fa-chevron-left" :disabled="page <= 1" @click="page--; load()" />
      <span class="tabular-nums">{{ page }} / {{ lastPage }}</span>
      <UiButton size="xs" icon="fa-chevron-right" :disabled="page >= lastPage" @click="page++; load()" />
    </footer>
    <QuickCampaignDialog :open="quick" :list="record" :count="total" @close="quick = false" @done="quick = false; emit('changed')" />
  </section>
</template>
